import { Component, Input, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { Tenant } from '../../../models/tenant';
import { finalize, Subject, takeUntil } from 'rxjs';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { AuxiliarService } from '../../../services/auxiliar.service';
import { NGXLogger } from 'ngx-logger';
import { TranslateService } from '@ngx-translate/core';
import { TenantService } from '../../../services/tenant.service';
import _ from 'lodash';
import { duplicatedValidator } from '../../../utils/validators/duplicateValidator';
import { environment } from '../../../../environments/environment';

@Component({
  selector: 'app-tenant-drawer',
  standalone: false,
  templateUrl: './tenant-drawer.component.html',
  styleUrl: './tenant-drawer.component.scss',
  encapsulation: ViewEncapsulation.None,
})
export class TenantDrawerComponent implements OnInit, OnDestroy {
  @Input() edit: boolean;
  @Input() tenant: Tenant;
  @Input() tenants: Tenant[] = [];

  private destroy$: Subject<boolean> = new Subject<boolean>();
  public form: FormGroup;
  public loading: boolean;

  get f(): { [index: string]: any } {
    return this.form.controls;
  }

  constructor(
    private formBuilder: FormBuilder,
    private auxiliarService: AuxiliarService,
    private logger: NGXLogger,
    private translate: TranslateService,
    private tenantService: TenantService,
  ){}

  ngOnInit(): void {
    // load form
    this.loadForm();
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  private loadForm(): void {
    const existingNames = _.filter(_.map(_.clone(this.tenants), 'name'), (tenant) => tenant !== this.tenant?.name);
    const existingNicknames = _.filter(_.map(_.clone(this.tenants), 'nickname'), (tenant) => tenant !== this.tenant?.nickname);

    this.form = this.formBuilder.group({
      address: [this.tenant?.address || '', [Validators.required]],
      bankAccount: [this.tenant?.bankAccount || '', []],
      bankName: [this.tenant?.bankName || '', []],
      bankSWIFT: [this.tenant?.bankSWIFT || '', []],
      city: [this.tenant?.city || '', [Validators.required]],
      email: [this.tenant?.email || '', [Validators.email]],
      emailBilling: [this.tenant?.emailBilling || '', [Validators.email]],
      id: [this.tenant?.id, []],
      invoiceSeries: [this.tenant?.invoiceSeries, [Validators.required]],
      legalcode: [this.tenant?.legalcode || '', []],
      name: [this.tenant?.name || '', [Validators.required, duplicatedValidator(existingNames)]],
      nickname: [this.tenant?.nickname || '', [duplicatedValidator(existingNicknames)]],
      phone: [this.formatPhone(this.tenant?.phone), [Validators.pattern(environment.pattern.phone)]],
      phoneWhatsapp: [this.formatPhone(this.tenant?.phoneWhatsapp), [Validators.pattern(environment.pattern.phone)]],
      state: [this.tenant?.state || '', [Validators.required]],
      taxSpecialReason: [this.tenant?.taxSpecialReason || '', []],
      website: [this.tenant?.website || '', []],
      zipcode: [this.tenant?.zipcode || '', [Validators.required]],
    });

    if(this.tenant) {
      this.f['id'].setValidators([Validators.required]);
    }

    if(!this.edit) {
      this.form.disable();
    }
  }

  private formatPhone(phone: string): string {
    if(!phone) return '';
    return _.replace(phone, '+34', '');
  }

  public save(): void {
    this.form.markAllAsTouched();
    if (this.form.valid) {
      this.loading = true;
      const formData = this.form.getRawValue();
      if(formData.phone) {
        _.set(formData, 'phone', `+34${formData.phone}`);
      }
      if(formData.phoneWhatsapp) {
        _.set(formData, 'phoneWhatsapp', `+34${formData.phoneWhatsapp}`);
      }
      if(this.tenant) {
        // update tenant
        this.tenantService.updateTenant(formData).pipe(
          finalize(() => this.loading = false),
          takeUntil(this.destroy$)
        ).subscribe({
          next: (res: any) => {
            this.logger.debug('[tenantService] updateTenant res: ', res);
            this.auxiliarService.toast(this.translate.instant('success.tenant-update'), 'success');
            this.auxiliarService.closeDrawer('right', _.get(res, 'body'));
          },
          error: (err: any) => {
            this.logger.error('[tenantService] updateTenant err:', err);
            this.auxiliarService.toast(this.translate.instant('error.tenant-update'), 'error');
          }
        });
      } else {
        // create tenant
        delete formData.id;
        this.tenantService.createTenant(formData).pipe(
          finalize(() => this.loading = false),
          takeUntil(this.destroy$)
        ).subscribe({
          next: (res: any) => {
            this.logger.debug('[tenantService] createTenant res: ', res);
            this.auxiliarService.toast(this.translate.instant('success.tenant-create'), 'success');
            this.auxiliarService.closeDrawer('right', _.get(res, 'body'));
          },
          error: (err: any) => {
            this.logger.error('[tenantService] createTenant err:', err);
            this.auxiliarService.toast(this.translate.instant('error.tenant-create'), 'error');
          }
        });
      }
    }
  }

  public cancel(): void {
    this.auxiliarService.closeDrawer('right');
  }
}
